/**
 * The client chat screen's thread (client-chat-screen D4, D7; client-chat-thread.html frames
 * 1, 3 and 4). Two shapes: the client's own messages on the right in the accent gradient, the
 * business on the left on strong glass. No lock marks on bubbles: the room is the mark.
 *
 * - Secure: the client's messages carry a tick once stored (a tick means stored, nothing more;
 *   the client never sees whether the business read them). A send the server did not store
 *   shows no tick and a line under it.
 * - History: the client's Telegram messages, the business's Telegram replies, every delivered
 *   notification as an automatic message (the text the client got, buttons dropped, one door
 *   in the footer, a form request's state line, a reminder's preparation pages folded), and
 *   media the client sent as a quiet line. No delivery states anywhere.
 * - Shared with the phone website (shared-client-screens D3, rows M7, M20): `markOf` draws the
 *   room beside each time (the website's one stream), and a link inside a notice opens through
 *   the `openLink` its screen is given (Telegram's opener in the app, a new tab on the website).
 * - Shared with the admin thread (Gabriel 2026-10-06): a long automatic message clamps at about
 *   15 lines with Show more (ClampedBody), the preparation fold opens with the admin's motion
 *   (FoldPanel), and a failure line opens under its bubble without a jump (RevealLine).
 */
import { Fragment, useState, type MouseEvent } from 'react';
import type { AutomaticMessageState, ClientAutomaticPayload, ClientDoor, ClientThreadEntry } from '@telify/shared';
import {
  clientDelivery, clientDoorLabel, clientMediaLine, clientStateLine, formatClientTime, groupClientDays, type ClientSendIssue,
} from '@/lib/client/clientChatThread';
import { ClampedBody } from '@/components/chat/ClampedBody';
import { FoldPanel } from '@/components/chat/FoldPanel';
import { RevealLine } from '@/components/chat/RevealLine';
import styles from './ClientChatScreen.module.css';

const cx = (...names: Array<string | false | null | undefined>) => names.filter(Boolean).join(' ');

const TickIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-label="Stored" role="img">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const PlaneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4 20-7z" />
  </svg>
);

/** The room an entry is in, drawn beside its time: the lock for Secure, the Telegram plane for the rest. */
export type ChatMark = 'secure' | 'telegram';

function RoomMark({ mark }: { mark: ChatMark | null }) {
  if (!mark) return null;
  return (
    <span className={styles.mark} role="img" aria-label={mark === 'secure' ? 'Secure' : 'Telegram'}>
      {mark === 'secure' ? <LockIcon /> : <PlaneIcon />}
    </span>
  );
}

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

export interface ClientChatTimelineProps {
  entries: ClientThreadEntry[];
  timezone: string;
  /** Whether the entry is a secure message: the client's stored ones carry a tick. */
  secureOf: (entry: ClientThreadEntry) => boolean;
  /** The room mark beside the entry's time; none when absent (the app: the room is the mark). */
  markOf?: (entry: ClientThreadEntry) => ChatMark;
  /** A footer door was tapped: open its page. */
  onDoor: (door: ClientDoor) => void;
  /** Opens a link inside a notice outside the screen. */
  openLink: (href: string) => void;
  issueOf: (entry: ClientThreadEntry) => ClientSendIssue | null;
  onRetry: (key: string) => void;
}

export function ClientChatTimeline({ entries, timezone, secureOf, markOf, onDoor, openLink, issueOf, onRetry }: ClientChatTimelineProps) {
  return (
    <>
      {groupClientDays(entries, timezone).map((day) => (
        <Fragment key={day.key}>
          <div className={styles.day} role="separator">{day.label}</div>
          {day.entries.map((entry) => (
            <EntryView
              // A sent message keeps one node from placeholder to stored row, so its landing is never cut.
              key={entry.kind === 'chat-message' && entry.idempotencyKey ? entry.idempotencyKey : entry.id}
              entry={entry}
              timezone={timezone}
              secure={secureOf(entry)}
              mark={markOf?.(entry) ?? null}
              onDoor={onDoor}
              openLink={openLink}
              issue={issueOf(entry)}
              onRetry={onRetry}
            />
          ))}
        </Fragment>
      ))}
    </>
  );
}

function EntryView({
  entry, timezone, secure, mark, onDoor, openLink, issue, onRetry,
}: {
  entry: ClientThreadEntry;
  timezone: string;
  secure: boolean;
  mark: ChatMark | null;
  onDoor: (door: ClientDoor) => void;
  openLink: (href: string) => void;
  issue: ClientSendIssue | null;
  onRetry: (key: string) => void;
}) {
  const time = formatClientTime(entry.at, timezone);
  switch (entry.kind) {
    case 'chat-media':
      return <div className={styles.sys} data-entry-id={entry.id}><RoomMark mark={mark} />{clientMediaLine(entry.payload.mediaType)}</div>;
    case 'automatic':
      return (
        <AutomaticBubble
          entryId={entry.id}
          payload={entry.payload}
          time={time}
          timezone={timezone}
          mark={mark}
          onDoor={onDoor}
          openLink={openLink}
        />
      );
    case 'reply':
      return (
        <div className={cx(styles.msg, styles.them)} data-entry-id={entry.id}>
          <span className={styles.text}>{entry.payload.text}</span>
          <div className={styles.meta}><RoomMark mark={mark} />{time}</div>
        </div>
      );
    case 'chat-message': {
      const mine = entry.direction === 'inbound';
      const delivery = clientDelivery(entry, secure, issue);
      return (
        <>
          <div
            className={cx(styles.msg, mine ? styles.me : styles.them)}
            data-entry-id={entry.id}
            data-testid={mine ? 'chat-own-message' : 'chat-business-message'}
          >
            <span className={styles.text}>{entry.payload.text}</span>
            <div className={styles.meta}>
              <RoomMark mark={mark} />
              {/* "Sending…" until stored, as the admin's bubble (Gabriel 2026-10-06); a failed send shows its time. */}
              {delivery === 'sending' ? 'Sending…' : time}
              {delivery === 'stored' && <TickIcon />}
            </div>
          </div>
          {issue === 'unsent' && (
            <RevealLine className={styles.failReveal}>
              <button type="button" className={styles.failNote} onClick={() => onRetry(entry.id)}>
                <AlertIcon />
                Not sent. Tap to try again.
              </button>
            </RevealLine>
          )}
          {issue === 'refused' && (
            <RevealLine className={styles.failReveal}>
              <span className={styles.failNote} role="status">
                <AlertIcon />
                Not sent
              </span>
            </RevealLine>
          )}
        </>
      );
    }
  }
}

/** Server-converted display-safe copy; a link inside opens externally. */
function SentText({ html, label }: { html: string | null; label: string }) {
  if (html === null) return <>{label}</>;
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function linkInterceptor(openLink: (href: string) => void) {
  return (event: MouseEvent) => {
    const anchor = (event.target as Element).closest?.('a');
    if (!anchor) return;
    event.preventDefault();
    const href = anchor.getAttribute('href');
    if (href && /^https?:\/\//i.test(href)) openLink(href);
  };
}

function AutomaticBubble({
  entryId, payload, time, timezone, mark, onDoor, openLink,
}: {
  entryId: string;
  payload: ClientAutomaticPayload;
  time: string;
  timezone: string;
  mark: ChatMark | null;
  onDoor: (door: ClientDoor) => void;
  openLink: (href: string) => void;
}) {
  const [prepOpen, setPrepOpen] = useState(false);
  const interceptLink = linkInterceptor(openLink);
  const door = payload.clientDoor;
  const pages = payload.preparation ?? [];
  return (
    <div className={cx(styles.msg, styles.them, styles.auto)} data-entry-id={entryId} data-testid="chat-automatic-message">
      <ClampedBody className={styles.autoBody} moreClassName={styles.showMore} measureKey={payload.text} onClick={interceptLink}>
        <SentText html={payload.text} label={payload.label} />
      </ClampedBody>
      {payload.state && <StateLine state={payload.state} timezone={timezone} />}
      {pages.length > 0 && (
        <>
          <button type="button" className={styles.fold} aria-expanded={prepOpen} onClick={() => setPrepOpen((open) => !open)}>
            {payload.preparationLabel || 'How to prepare'}
            <ChevronRight />
          </button>
          <FoldPanel open={prepOpen}>
            {pages.map((page) => (
              <div key={page.id} className={styles.foldPage} onClick={interceptLink}>
                <SentText html={page.text} label="How to prepare" />
              </div>
            ))}
          </FoldPanel>
        </>
      )}
      <div className={cx(styles.foot, !door && styles.footEnd)}>
        {door && (
          <button type="button" className={styles.door} onClick={() => onDoor(door)}>
            {clientDoorLabel(door)}
            <ChevronRight />
          </button>
        )}
        <span className={styles.time}><RoomMark mark={mark} />{time}</span>
      </div>
    </div>
  );
}

/** A form request's or an invite's state (frame 4): Waiting for you · Submitted May 12 · Expired · Withdrawn. */
function StateLine({ state, timezone }: { state: AutomaticMessageState; timezone: string }) {
  const done = state.status === 'submitted' || state.status === 'booked';
  const waiting = state.status === 'awaiting' || state.status === 'pending';
  return (
    <div className={styles.state} data-testid="chat-state-line">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {done && <polyline points="20 6 9 17 4 12" />}
        {waiting && (
          <>
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </>
        )}
        {!done && !waiting && (
          <>
            <circle cx="12" cy="12" r="10" />
            <line x1="4.9" y1="4.9" x2="19.1" y2="19.1" />
          </>
        )}
      </svg>
      {clientStateLine(state, timezone)}
    </div>
  );
}
